/**
 * Protocolo de tempo real (WebSocket) entre cliente e servidor do RPG Desk.
 *
 * Regras de ouro:
 *  - O servidor é autoritativo. Todo estado (dados rolados, posição de token,
 *    ficha) que o cliente envia é validado e reemitido pelo servidor.
 *  - Toda mensagem é validada com zod antes de ser tratada.
 */
import { z } from 'zod';
import {
  ADMIN_LIMITS,
  ChatChannelSchema,
  ChatPersonaSchema,
  PvtGroupSchema,
  ChatMessageSchema,
  ContactViewSchema,
  FriendPresenceSchema,
  IdSchema,
  ImsMessageSchema,
  RoomMemberSchema,
  RoomRoleSchema,
  RollResultSchema,
  RoomSchema,
  UserProfileSchema,
  UserSchema,
  UserStatusSchema,
  DiceStyleSchema,
} from './domain.js';
import { AudioKindSchema, AudioRepeatSchema, AudioSfxEventSchema, AudioStateSchema, AudioUrlSchema } from './audio.js';
import { GameSessionActionSchema, GameSessionSchema } from './gameSessions.js';
import { LibraryItemKindSchema, LibraryItemSchema, NdbBatchSchema, NdbSequencedBatchSchema, NdbSnapshotSchema, RoomPluginSchema } from './library.js';
import { RtcMediaSchema, RtcSignalSchema } from './rtc.js';
import { DiceEffectRulesSchema } from './dice/effects.js';
import { PlanViewSchema, PublicPlanCatalogSchema } from './plans.js';

export const PROTOCOL_VERSION = 1;

/** Limites defensivos para entrada não confiável. */
export const LIMITS = {
  chatText: 2000,
  diceExpression: 120,
  /** lançamentos por minuto por conexão */
  rollsPerMinute: 60,
  messagesPerMinute: 240,
} as const;

/* --------------------------------------------------------------- cliente -> servidor */

export const ClientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }),
  z.object({
    type: z.literal('room.join'),
    roomId: IdSchema,
    /** exigido apenas quando a sala tem senha (`hasPassword`) */
    password: z.string().max(120).nullable().default(null),
  }),
  z.object({
    type: z.literal('room.leave'),
    /** despedida do `/partir`, registrada no log */
    message: z.string().trim().max(200).optional(),
  }),
  /* ---------------------------------------------- papéis e moderação (docs/spec/room-roles.md) */
  /** muda o papel de alguém (mestre/jogador/espectador) */
  z.object({ type: z.literal('presence.setRole'), userId: IdSchema, role: RoomRoleSchema }),
  /** liga/desliga mute, blind ou voice */
  z.object({
    type: z.literal('presence.setFlag'),
    userId: IdSchema,
    flag: z.enum(['mute', 'blind', 'voice']),
    on: z.boolean(),
  }),
  /** expulsa (e opcionalmente bane) alguém presente */
  z.object({ type: z.literal('presence.kick'), userId: IdSchema, ban: z.boolean().default(false) }),
  /** `/nick`: null volta ao nome da conta */
  z.object({ type: z.literal('presence.nick'), nick: z.string().trim().min(1).max(40).nullable() }),
  /**
   * escolhe (ou tira) o personagem principal: uma ficha da biblioteca da mesa. `userId` (só
   * mestres): o de outra pessoa, entre as fichas dela
   */
  z.object({ type: z.literal('presence.mainCharacter'), itemId: IdSchema.nullable(), userId: IdSchema.optional() }),
  /** barrinha do cartão (a própria, ou de outro se for mestre); index de 0 a 3; max ≤ 0 tira a barra */
  z.object({
    type: z.literal('presence.bar'),
    userId: IdSchema.optional(),
    index: z.number().int().min(0).max(3),
    value: z.number().finite(),
    max: z.number().finite(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  }),
  /** linha de detalhe do cartão; texto vazio tira a linha */
  z.object({ type: z.literal('presence.line'), userId: IdSchema.optional(), index: z.number().int().min(0).max(3), text: z.string().max(120) }),
  /** liga/desliga o chat moderado */
  z.object({ type: z.literal('room.moderate'), on: z.boolean() }),
  /** "digitando…" */
  z.object({ type: z.literal('chat.typing'), typing: z.boolean() }),
  z.object({
    type: z.literal('chat.send'),
    text: z.string().min(1).max(LIMITS.chatText),
    kind: z.enum(['chat', 'action', 'laugh', 'ooc']).default('chat'),
    persona: ChatPersonaSchema.optional(),
    /** conversa privada ou em grupo; ausente = chat principal */
    channel: ChatChannelSchema.optional(),
  }),
  /** um mestre apaga uma mensagem ou rolagem do chat da mesa (o id da linha; docs/spec/chat-delete.md) */
  z.object({ type: z.literal('chat.delete'), id: IdSchema }),
  /** um mestre limpa o chat da mesa inteiro */
  z.object({ type: z.literal('chat.clear') }),
  /** arquivo já enviado ao Desk Cloud, compartilhado no chat (texto = legenda opcional) */
  z.object({
    type: z.literal('chat.attach'),
    fileId: IdSchema,
    caption: z.string().max(LIMITS.chatText).default(''),
    channel: ChatChannelSchema.optional(),
  }),
  z.object({
    type: z.literal('dice.roll'),
    expression: z.string().min(1).max(LIMITS.diceExpression),
    label: z.string().max(80).nullable().default(null),
    channel: ChatChannelSchema.optional(),
    /** referência do cliente, devolvida no resultado (rolagens pedidas por plugins) */
    ref: z.string().max(80).optional(),
    /** cores dos dados 3D de quem rola */
    style: DiceStyleSchema.optional(),
    /** efeitos especiais dos dados de quem rola (docs/spec/dice-3d.md) */
    effects: DiceEffectRulesSchema.optional(),
  }),
  /* -------------------------------------------- conversa privada em grupo (Room_GroupPVT_*) */
  z.object({ type: z.literal('group.create'), userIds: z.array(IdSchema).min(1).max(30) }),
  z.object({ type: z.literal('group.invite'), groupId: IdSchema, userIds: z.array(IdSchema).min(1).max(30) }),
  z.object({ type: z.literal('group.part'), groupId: IdSchema }),
  /* -------------------------------------------------------- biblioteca e fichas (NDB) */
  z.object({
    type: z.literal('library.create'),
    kind: LibraryItemKindSchema,
    name: z.string().trim().min(1).max(80),
    parentId: IdSchema.nullable().default(null),
    dataType: z.string().max(120).nullable().default(null),
    /** só mestres criam ficha para outra pessoa */
    ownerId: IdSchema.nullable().optional(),
  }),
  z.object({ type: z.literal('library.rename'), itemId: IdSchema, name: z.string().trim().min(1).max(80) }),
  z.object({ type: z.literal('library.move'), itemId: IdSchema, parentId: IdSchema.nullable(), position: z.number().int().optional() }),
  z.object({ type: z.literal('library.delete'), itemId: IdSchema }),
  /** só mestres: passa a ficha para alguém da mesa (null: ninguém, só os mestres editam) */
  z.object({ type: z.literal('library.setOwner'), itemId: IdSchema, ownerId: IdSchema.nullable() }),
  /** desativar uma tabela da sala guarda os dados; ativar de novo traz a mesma tabela */
  z.object({ type: z.literal('library.dockActive'), itemId: IdSchema, active: z.boolean() }),
  /** só mestres: torna o item visível para todos ou o oculta dos jogadores (BRAZ-16) */
  z.object({ type: z.literal('library.setVisible'), itemId: IdSchema, visible: z.boolean() }),
  /** abre a ficha: o servidor manda o estado (ou só o que falta desde `sinceSeq`) e passa a mandar os lotes */
  z.object({ type: z.literal('ndb.open'), docId: IdSchema, sinceSeq: z.number().int().min(0).optional() }),
  z.object({ type: z.literal('ndb.close'), docId: IdSchema }),
  z.object({ type: z.literal('ndb.submit'), docId: IdSchema, batch: NdbBatchSchema }),
  z.object({ type: z.literal('plugin.uninstall'), moduleId: z.string().min(1).max(120) }),
  /** plugin web (docs/spec/web-plugins.md): o servidor lê o manifesto do próprio arquivo */
  z.object({ type: z.literal('plugin.installWeb'), fileId: IdSchema, ref: z.string().min(1).max(80).optional() }),
  /** plugin oficial (docs/spec/web-plugins.md, "Plugins oficiais"): a versão publicada agora, sem arquivo do mestre */
  z.object({ type: z.literal('plugin.installOfficial'), officialId: IdSchema, ref: z.string().min(1).max(80).optional() }),
  /** webhooks de um plugin web: o mestre vê e cadastra os endereços; a página pede o envio */
  z.object({ type: z.literal('plugin.webhooks.get'), moduleId: z.string().min(1).max(120) }),
  z.object({
    type: z.literal('plugin.webhook.set'),
    moduleId: z.string().min(1).max(120),
    name: z.string().min(1).max(40),
    url: z.string().trim().max(2000).nullable(),
    who: z.enum(['gm', 'everyone']),
  }),
  z.object({ type: z.literal('plugin.webhook.send'), ref: z.string().min(1).max(80), moduleId: z.string().min(1).max(120), name: z.string().min(1).max(40), data: z.unknown() }),
  /* ------------------------------------------------------------------ áudio */

  /* Áudio da mesa: os mestres montam a playlist e o quadro de efeitos e controlam a reprodução. */
  z.object({
    type: z.literal('audio.enqueue'),
    url: AudioUrlSchema,
    title: z.string().trim().min(1).max(120),
    kind: AudioKindSchema.default('music'),
  }),
  z
    .object({
      type: z.literal('audio.control'),
      action: z.enum(['play', 'pause', 'stop', 'next', 'prev', 'seek', 'select', 'ended']),
      positionSeconds: z.number().min(0).max(86_400).optional(),
      trackId: IdSchema.optional(),
    })
    .refine((value) => value.action !== 'seek' || value.positionSeconds !== undefined, { message: 'seek exige positionSeconds', path: ['positionSeconds'] })
    .refine((value) => (value.action !== 'select' && value.action !== 'ended') || value.trackId !== undefined, { message: 'informe a faixa', path: ['trackId'] }),
  z.object({ type: z.literal('audio.remove'), trackId: IdSchema }),
  z.object({ type: z.literal('audio.clear') }),
  z.object({ type: z.literal('audio.volume'), volume: z.number().min(0).max(1) }),
  z.object({ type: z.literal('audio.repeat'), repeat: AudioRepeatSchema }),
  /** dispara um efeito do quadro para toda a mesa */
  z.object({ type: z.literal('audio.sfx'), trackId: IdSchema }),

  /* ------------------------------------------------------- relógio da sessão */

  /**
   * Os mestres começam, pausam, voltam e encerram a sessão de jogo (docs/spec/session-clock.md).
   * `number`: a sessão em que clicaram (no começar, a próxima); o clique de uma tela desatualizada
   * não mexe em outra sessão.
   */
  z.object({ type: z.literal('gameSession.control'), action: GameSessionActionSchema, number: z.number().int().min(1).max(1_000_000) }),

  /* -------------------------------------------------------- voz e vídeo (WebRTC) */

  /** Sinalização endereçada: o servidor só repassa para o jogador alvo. */
  z.object({
    type: z.literal('rtc.signal'),
    targetUserId: IdSchema,
    signal: RtcSignalSchema,
  }),
  /** O que estou publicando (para os outros mostrarem os ícones certos). */
  z.object({
    type: z.literal('rtc.media'),
    media: RtcMediaSchema,
  }),

  /* -------------------------------------------------------- amigos (fora de sala) */

  /** Esta janela está sem uso há `FRIENDS_LIMITS.awayAfterMs` (ou voltou a ser usada). */
  z.object({ type: z.literal('presence.away'), away: z.boolean() }),
  /** O status que escolhi na minha foto (vale para a conta, em todas as janelas). */
  z.object({ type: z.literal('presence.status'), status: UserStatusSchema }),
  /** "Digitando…" na conversa com um amigo; o servidor só repassa entre amigos. */
  z.object({ type: z.literal('ims.typing'), targetUserId: IdSchema, typing: z.boolean() }),

  /* -------------------------------------------------------- cena */

  /**
   * "Ping" num ponto da cena (em células), para quem estiver olhando a cena.
   * `focus` (só mestres): leva a vista de todos até o ponto.
   */
  z.object({
    type: z.literal('scene.ping'),
    itemId: IdSchema,
    x: z.number().finite().min(-10_000).max(10_000),
    y: z.number().finite().min(-10_000).max(10_000),
    focus: z.boolean().default(false),
  }),
  /**
   * Abre ou fecha uma porta da cena (`docs/spec/scene-walls.md`). O servidor confere a porta, que
   * não está trancada e que não é secreta (para quem não é mestre), e grava ele mesmo.
   */
  z.object({ type: z.literal('scene.door'), itemId: IdSchema, wallId: IdSchema, open: z.boolean() }),
  /**
   * Liga, muda ou tira a condição "Invisível" de um token (BRAZ-24): `viewers` são os liberados para
   * vê-lo, além dos mestres e de quem o controla; null tira a condição. Vale para o mestre, em
   * qualquer token, e para quem controla o token, no dele. O servidor confere e grava ele mesmo,
   * com as permissões de leitura (quem não vê nem recebe o token).
   */
  z.object({ type: z.literal('scene.invisible'), itemId: IdSchema, tokenId: IdSchema, viewers: z.array(IdSchema).max(50).nullable() }),
]);

/**
 * O que o cliente ENVIA: campos com `.default()` são opcionais aqui.
 * Use este tipo para construir mensagens.
 */
export type ClientMessage = z.input<typeof ClientMessageSchema>;
export type ClientMessageType = ClientMessage['type'];

/**
 * O que o servidor RECEBE já validado: defaults aplicados, campos opcionais
 * preenchidos. É este o tipo que os handlers do servidor devem aceitar.
 */
export type ClientMessagePayload = z.output<typeof ClientMessageSchema>;

/* --------------------------------------------------------------- servidor -> cliente */

export const RoomStateSchema = z.object({
  room: RoomSchema,
  /** membros atualmente conectados na sala */
  users: z.array(UserSchema),
  /** papel, modos e apelido de cada membro conectado (mesma ordem de `users`) */
  members: z.array(RoomMemberSchema).default([]),
  /** estado do áudio compartilhado */
  audio: AudioStateSchema,
  /** a última sessão de jogo da mesa (aberta ou terminada); null se a mesa nunca teve uma */
  gameSession: GameSessionSchema.nullable().default(null),
  /** o que cada participante está publicando em voz/vídeo */
  media: z.array(z.object({ userId: IdSchema, media: RtcMediaSchema })),
});
export type RoomState = z.infer<typeof RoomStateSchema>;

export const ServerMessageSchema = z.discriminatedUnion('type', [
  /**
   * Primeira mensagem após autenticar: identifica quem conectou, SEM entrar em
   * sala. Entrar é um passo explícito (`room.join`), o que permite verificar a
   * senha da sala no servidor — antes isso era possível contornar pela query
   * string do WebSocket.
   */
  z.object({
    type: z.literal('hello'),
    protocolVersion: z.number().int(),
    self: UserSchema,
    serverTime: z.number().int(),
    /** o que este servidor oferece além do básico */
    features: z
      .object({
        randomOrg: z.boolean(),
        /** presença dos amigos (ausente, mesa), "digitando…" e convites (docs/spec/friends.md) */
        presence: z.boolean().optional(),
        /** mensagens diretas com quem não é amigo, com a privacidade "só amigos" (docs/spec/friends.md) */
        imsAnyone: z.boolean().optional(),
        /** relógio da sessão (docs/spec/session-clock.md); sem ele, o cliente não mostra o relógio */
        gameSessions: z.boolean().optional(),
        /** as avaliações das sessões (docs/spec/session-clock.md, "Avaliações") */
        sessionRatings: z.boolean().optional(),
        /** os mestres apagam linhas do chat, limpam o chat e usam o /audit (docs/spec/chat-delete.md) */
        chatDelete: z.boolean().optional(),
      })
      .optional(),
    /** o status que escolhi (docs/spec/friends.md, "Status"); sem ele, o servidor não tem status */
    status: UserStatusSchema.optional(),
  }),
  /** primeira mensagem após autenticar: estado completo da sala */
  z.object({
    type: z.literal('ready'),
    protocolVersion: z.number().int(),
    self: UserSchema,
    state: RoomStateSchema,
    /** últimas mensagens para preencher o histórico */
    history: z.array(ChatMessageSchema).max(200),
    /** rolagens recentes, para o chat mostrar o detalhe das rolagens do histórico */
    rollHistory: z.array(RollResultSchema).max(400).default([]),
    /** conversas privadas e em grupo de quem entrou (últimas mensagens) */
    privateHistory: z.array(ChatMessageSchema).max(400).default([]),
    /** grupos privados de que faço parte nesta mesa */
    groups: z.array(PvtGroupSchema).default([]),
    /** itens da biblioteca da sala */
    library: z.array(LibraryItemSchema).default([]),
    /** plugins instalados na mesa */
    plugins: z.array(RoomPluginSchema).default([]),
    serverTime: z.number().int(),
  }),
  z.object({ type: z.literal('pong'), serverTime: z.number().int() }),
  z.object({
    type: z.literal('error'),
    code: z.enum([
      'bad-message',
      'not-authenticated',
      'not-in-room',
      'forbidden',
      'rate-limited',
      'not-found',
      'internal',
      /** a conta está banida desta sala */
      'banned',
      /** senha da sala ausente ou errada */
      'wrong-password',
      /** sem permissão para falar agora (mute ou chat moderado) */
      'muted',
    ]),
    message: z.string().max(300),
  }),
  z.object({ type: z.literal('presence.join'), user: UserSchema, member: RoomMemberSchema.optional() }),
  z.object({ type: z.literal('presence.leave'), userId: IdSchema }),
  /** modos ou apelido de alguém mudaram */
  z.object({ type: z.literal('presence.updated'), member: RoomMemberSchema }),
  z.object({ type: z.literal('presence.typing'), userId: IdSchema, typing: z.boolean() }),
  /** você foi tirado da sala */
  z.object({
    type: z.literal('room.removed'),
    reason: z.enum(['kicked', 'banned', 'deleted']),
    byName: z.string().max(40),
  }),
  /** dados da sala mudaram (moderação, perfil) */
  z.object({ type: z.literal('room.updated'), room: RoomSchema }),
  /** um grupo privado de que faço parte foi criado ou mudou (membros) */
  z.object({ type: z.literal('group.state'), group: PvtGroupSchema }),
  /** saí de um grupo (ou ele deixou de existir para mim) */
  z.object({ type: z.literal('group.left'), groupId: IdSchema }),
  /* -------------------------------------------------------- biblioteca e fichas (NDB) */
  z.object({ type: z.literal('library.item'), item: LibraryItemSchema }),
  z.object({ type: z.literal('plugins.state'), plugins: z.array(RoomPluginSchema) }),
  /** só para mestres: os endereços cadastrados e os últimos envios */
  z.object({
    type: z.literal('plugin.webhooks'),
    moduleId: z.string().max(120),
    hooks: z.array(z.object({ name: z.string().max(40), url: z.string().max(2000), who: z.enum(['gm', 'everyone']) })).max(50),
    log: z
      .array(z.object({ at: z.number(), nick: z.string().max(80), name: z.string().max(40), ok: z.boolean(), status: z.number().int(), error: z.string().max(300).nullable() }))
      .max(50),
  }),
  /** resposta ao `plugin.installWeb` e ao `plugin.installOfficial` com `ref` (a lista nova chega em `plugins.state`) */
  z.object({ type: z.literal('plugin.installResult'), ref: z.string().max(80), ok: z.boolean(), error: z.string().max(300).nullable() }),
  z.object({ type: z.literal('plugin.webhook.result'), ref: z.string().max(80), ok: z.boolean(), status: z.number().int(), body: z.unknown(), error: z.string().max(300).nullable() }),
  z.object({ type: z.literal('library.removed'), itemIds: z.array(IdSchema) }),
  /**
   * itens que existem, mas deixaram de estar visíveis para mim (BRAZ-16): saem da biblioteca, e o
   * que estava aberto vira um bloco preto até voltar a ser visível (`library.item`)
   */
  z.object({ type: z.literal('library.hidden'), itemIds: z.array(IdSchema) }),
  /** estado completo da ficha (como esta pessoa pode ver) */
  z.object({ type: z.literal('ndb.snapshot'), docId: IdSchema, snapshot: NdbSnapshotSchema, seq: z.number().int().min(0) }),
  /** lotes aceitos, em ordem (inclusive os meus, que confirmam as escritas otimistas) */
  z.object({ type: z.literal('ndb.batches'), docId: IdSchema, batches: z.array(NdbSequencedBatchSchema) }),
  /** meu lote foi recusado (permissão, nó inexistente…) */
  z.object({ type: z.literal('ndb.reject'), docId: IdSchema, clientSeq: z.number().int(), error: z.string().max(300) }),
  /**
   * meu lote, reenviado ao reconectar, já estava aplicado: o estado que recebi já o tem, e ele sai da
   * fila (a reposição por snapshot não confirma os lotes que traz)
   */
  z.object({ type: z.literal('ndb.applied'), docId: IdSchema, clientSeq: z.number().int() }),
  z.object({ type: z.literal('chat.message'), message: ChatMessageSchema }),
  /**
   * linhas que saíram do histórico do chat (as de uma sessão apagada, as que um mestre apagou ou
   * limpou): quem as tem na tela as tira. `rollIds`: as rolagens que saíram junto (o detalhe delas)
   */
  z.object({ type: z.literal('chat.removed'), roomId: IdSchema, ids: z.array(IdSchema).max(1000), rollIds: z.array(IdSchema).max(1000).optional() }),
  z.object({ type: z.literal('dice.result'), roll: RollResultSchema }),

  /* ------------------------------------------------------------------ áudio */

  /** estado completo do áudio: enviado na entrada e a cada mudança */
  z.object({ type: z.literal('audio.state'), audio: AudioStateSchema }),
  /** efeito sonoro pontual, para tocar uma vez em todos os clientes */
  z.object({ type: z.literal('audio.sfx'), event: AudioSfxEventSchema }),
  /** o relógio da sessão mudou: a última sessão da mesa (aberta ou a que acabou de terminar) */
  z.object({ type: z.literal('gameSession.state'), roomId: IdSchema, session: GameSessionSchema.nullable() }),
  /** o histórico das sessões da mesa mudou (título, notas ou avaliações): a aba Sessões busca de novo */
  z.object({ type: z.literal('gameSession.history'), roomId: IdSchema }),
  /** há avisos de avaliação para mim: o cliente os busca (`POST /api/me/session-notices`) */
  z.object({ type: z.literal('gameSession.notices') }),

  /* -------------------------------------------------------- voz e vídeo (WebRTC) */

  /** sinalização de outro jogador da sala (offer/answer/candidate) */
  z.object({ type: z.literal('rtc.signal'), fromUserId: IdSchema, signal: RtcSignalSchema }),
  /** alguém mudou o que está publicando */
  z.object({ type: z.literal('rtc.media'), userId: IdSchema, media: RtcMediaSchema }),
  /** ping numa cena (vai para todos da sala, inclusive quem pingou) */
  z.object({ type: z.literal('scene.ping'), itemId: IdSchema, userId: IdSchema, x: z.number(), y: z.number(), focus: z.boolean() }),
  /** porta trancada: vai para quem tentou abrir (chacoalha) e para os mestres (aviso) */
  z.object({ type: z.literal('scene.door.locked'), itemId: IdSchema, wallId: IdSchema, userId: IdSchema }),
  /** quem não é mestre ligou, mudou ou tirou o invisível de um token: vai para os mestres (BRAZ-24) */
  z.object({ type: z.literal('scene.invisible.notice'), itemId: IdSchema, tokenId: IdSchema, userId: IdSchema, change: z.enum(['on', 'viewers', 'off']) }),

  /* -------------------------------------------------------- contatos e mensagens */

  /**
   * mensagem direta recebida (entregue ao vivo, fora de sala). `from` é quem mandou: quem não é
   * amigo não está na lista, e o cliente precisa do nome e da foto (docs/spec/friends.md)
   */
  z.object({ type: z.literal('ims.message'), message: ImsMessageSchema, from: UserSchema.optional() }),
  /** a relação com alguém mudou: pedido, aceite, bloqueio… */
  z.object({ type: z.literal('contact.updated'), contact: ContactViewSchema }),
  /** alguém da mesa (ou eu) mudou nome/avatar */
  z.object({ type: z.literal('user.updated'), user: UserSchema }),
  /** a pessoa desfez a relação comigo (recusou, cancelou, bloqueou ou removeu) */
  z.object({ type: z.literal('contact.removed'), userId: IdSchema }),
  /** um amigo conectou, desconectou, ficou ausente ou entrou ou saiu de uma mesa */
  z.object({
    type: z.literal('contact.presence'),
    userId: IdSchema,
    online: z.boolean(),
    presence: FriendPresenceSchema.optional(),
    /** quando saiu (vem quando ele fica offline) */
    lastSeenAt: z.number().int().nonnegative().nullable().optional(),
  }),
  /** meu status mudou (escolhido nesta janela ou em outra) */
  z.object({ type: z.literal('presence.status'), status: UserStatusSchema }),
  /** o amigo está (ou parou de estar) digitando na conversa comigo */
  z.object({ type: z.literal('ims.typing'), userId: IdSchema, typing: z.boolean() }),
  /** li a conversa com alguém em outra janela: as não lidas dela zeram aqui também */
  z.object({ type: z.literal('ims.read'), userId: IdSchema }),
  /** tirei a conversa com alguém das "Outras conversas" em outra janela (some daqui também) */
  z.object({ type: z.literal('ims.dismissed'), userId: IdSchema }),
  /** o perfil de alguém mudou (ex.: alguém curtiu) */
  z.object({ type: z.literal('profile.updated'), profile: UserProfileSchema }),

  /* -------------------------------------------------------- conta (docs/spec/admin.md) */

  /**
   * Um administrador suspendeu a conta, trocou a senha dela ou a apagou, ou a senha foi trocada pelo
   * link de "Esqueci a senha" (`reset`; docs/spec/email.md): as sessões já não valem e o servidor
   * fecha a conexão logo depois. O cliente sai e mostra o motivo (`detail`: o da suspensão), em vez de
   * tentar reconectar com um token morto.
   */
  z.object({ type: z.literal('session.ended'), reason: z.enum(['suspended', 'password', 'deleted', 'reset']), detail: z.string().max(ADMIN_LIMITS.reason).optional() }),
  /** um administrador deu ou tirou desta conta o papel de administrador */
  z.object({ type: z.literal('account.admin'), admin: z.boolean() }),
  /** um administrador mudou o plano desta conta (docs/spec/planos.md): o que vale agora */
  z.object({ type: z.literal('account.plan'), plan: PlanViewSchema }),
  /** um admin editou os planos (docs/spec/planos.md, "Editar os planos"): o catálogo novo, para todos */
  z.object({ type: z.literal('plans.updated'), plans: PublicPlanCatalogSchema }),

  /* -------------------------------------------------------- Desk Cloud (docs/spec/desk-cloud.md) */

  /** os arquivos ou as pastas desta conta mudaram (noutra aba, noutro aparelho): a tela relê */
  z.object({ type: z.literal('files.changed') }),
  /** mudaram os arquivos ligados a esta mesa: o painel Arquivos relê (só o aviso, sem o conteúdo) */
  z.object({ type: z.literal('room.files'), roomId: IdSchema }),
]);

export type ServerMessage = z.output<typeof ServerMessageSchema>;
export type ServerMessageType = ServerMessage['type'];

/* --------------------------------------------------------------- helpers de parsing */

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

/** Valida uma mensagem crua vinda do cliente. Nunca lança. */
export function parseClientMessage(raw: unknown): ParseResult<ClientMessagePayload> {
  const parsed = ClientMessageSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const where = first?.path.join('.') ?? '';
    const detail = first?.message ?? 'formato inválido';
    return { ok: false, error: where ? `${where}: ${detail}` : detail };
  }
  return { ok: true, value: parsed.data };
}

/** Valida uma mensagem recebida do servidor (usado no cliente). */
export function parseServerMessage(raw: unknown): ParseResult<ServerMessage> {
  const parsed = ServerMessageSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    return { ok: false, error: first?.message ?? 'formato inválido' };
  }
  return { ok: true, value: parsed.data };
}
